import { useMemo } from "react";
import { toast } from "sonner";
import { InfoWithLabel } from "@/core/components/info-with-label";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { useBusiness } from "@/core/business";
import { formatMoney, formatMoneyCents } from "@/core/utils/money";
import { useGetLatestSpiffRules } from "@/spiff-rules/query";
import { useCreateSpiff } from "@/spiffs/query";
import {
  IAttributedUserWithSpiff,
  IEnrichedActivityFeedItem,
} from "./activity-feed.d";

interface CreateAttributionSpiffModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: IEnrichedActivityFeedItem | null;
  attributedUser: IAttributedUserWithSpiff | null;
}

export const CreateAttributionSpiffModal = ({
  open,
  onOpenChange,
  activity,
  attributedUser,
}: CreateAttributionSpiffModalProps) => {
  const { businessId } = useBusiness();
  const { data: spiffRules = [] } = useGetLatestSpiffRules(businessId);
  const { mutate: createSpiff, isPending: isCreatingSpiff } =
    useCreateSpiff(businessId);

  // Find the matching spiff rule for this activity's event type
  const matchingSpiffRule = useMemo(() => {
    if (!activity) return null;
    return (
      spiffRules.find(
        (rule) => rule.event === activity.spiff_event_name && rule.active,
      ) || null
    );
  }, [spiffRules, activity]);

  // Format the spiff amount display text
  const spiffAmountDisplay = useMemo(() => {
    if (!matchingSpiffRule) return "$0";

    if (matchingSpiffRule.payment_amount_cents) {
      return formatMoneyCents(matchingSpiffRule.payment_amount_cents);
    }

    if (matchingSpiffRule.payment_percent) {
      const percentText = `${matchingSpiffRule.payment_percent}%`;
      if (matchingSpiffRule.payment_percent_cap_cents) {
        return `${percentText} (up to ${formatMoney(matchingSpiffRule.payment_percent_cap_cents / 100)})`;
      }
      return percentText;
    }

    return "$0";
  }, [matchingSpiffRule]);

  const handleConfirm = async () => {
    if (!activity || !attributedUser || !matchingSpiffRule) {
      toast.error("Missing required information to create spiff");
      return;
    }

    createSpiff(
      {
        userId: attributedUser.userId,
        triggerId: activity.underlying_record_id,
        triggerType: activity.underlying_record_type,
        event: activity.spiff_event_name,
      },
      {
        onSuccess: () => {
          toast.success("Spiff created successfully");
          onOpenChange(false);
        },
        onError: (error: Error) => {
          toast.error(error.message || "Failed to create spiff");
        },
      },
    );
  };

  const userName = attributedUser?.isCurrentUser
    ? "You"
    : attributedUser?.displayIdentification || "Unknown user";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent description="Confirm spiff creation" className="gap-0">
        <DialogHeader>
          <DialogTitle>Create Spiff</DialogTitle>
          <DialogDescription>
            Review the spiff details below and confirm to create.
          </DialogDescription>
        </DialogHeader>
        <Stack gap="lg" className="p-6 pt-0">
          <InfoWithLabel label="User">{userName}</InfoWithLabel>
          <InfoWithLabel label="Spiff Amount">
            {spiffAmountDisplay}
          </InfoWithLabel>
        </Stack>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isCreatingSpiff}
            loading={isCreatingSpiff}
          >
            Create Spiff
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
